import { useEffect, useState } from 'react';
import { useTerritoryStore } from '../../../store/territoryStore';
import { useUserStore } from '../../../store/userStore';
import type { TerritoryCreate, TerritoryType } from '../../../types';

interface TerritoryFormProps {
  regionId?: string;
  onClose: () => void;
}

const US_STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA',
  'HI','ID','IL','IN','IA','KS','KY','LA','ME','MD',
  'MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ',
  'NM','NY','NC','ND','OH','OK','OR','PA','RI','SC',
  'SD','TN','TX','UT','VT','VA','WA','WV','WI','WY',
  'DC',
];

export default function TerritoryForm({ regionId: initialRegionId, onClose }: TerritoryFormProps) {
  const { regions, createTerritory } = useTerritoryStore();
  const { users, fetchUsers } = useUserStore();

  const [name, setName] = useState('');
  const [regionId, setRegionId] = useState(initialRegionId ?? '');
  const [assignedUserId, setAssignedUserId] = useState('');
  const [type, setType] = useState<TerritoryType>('STATE');
  const [selectedStates, setSelectedStates] = useState<string[]>([]);
  const [zipInput, setZipInput] = useState('');
  const [zipCodes, setZipCodes] = useState<string[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const activeRegions = regions.filter((r) => r.isActive);

  const toggleState = (code: string) => {
    setSelectedStates((prev) =>
      prev.includes(code) ? prev.filter((s) => s !== code) : [...prev, code].sort()
    );
  };

  // Display ZIP prefix: "90000" → "900xx", "90001" stays as "90001"
  const formatZip = (zip: string) => zip.endsWith('00') ? `${zip.substring(0, 3)}xx` : zip;

  const addZips = () => {
    const newZips = zipInput
      .split(/[\s,]+/)
      .map((z) => z.trim())
      .filter(Boolean)
      .map((z) => {
        if (/^\d{3}$/.test(z)) return `${z}00`;
        if (/^\d{5}$/.test(z)) return z;
        return null;
      })
      .filter((z): z is string => z !== null && !zipCodes.includes(z));
    if (newZips.length > 0) {
      setZipCodes((prev) => [...prev, ...newZips]);
    }
    setZipInput('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!name.trim()) return setError('Name is required.');
    if (!regionId) return setError('Region is required.');

    setIsSaving(true);
    try {
      const payload: TerritoryCreate = {
        name: name.trim(),
        regionId,
        territoryType: type,
        assignedUserId: assignedUserId || undefined,
        states: type === 'STATE' ? selectedStates : undefined,
        zipCodes: type === 'ZIP_CODE' ? zipCodes : undefined,
      };
      await createTerritory(payload);
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.message ?? 'Failed to create territory.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-5">
      <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100 mb-4">New Territory</h3>

      <form onSubmit={handleSubmit} className="space-y-4">
        {error && (
          <p className="text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded px-3 py-2">
            {error}
          </p>
        )}

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. New England States"
            className="w-full border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 dark:placeholder-gray-400 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Region <span className="text-red-500">*</span>
          </label>
          <select
            value={regionId}
            onChange={(e) => setRegionId(e.target.value)}
            className="w-full border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">Select region...</option>
            {activeRegions.map((r) => (
              <option key={r.id} value={r.id}>
                {r.name}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Assigned Rep</label>
          <select
            value={assignedUserId}
            onChange={(e) => setAssignedUserId(e.target.value)}
            className="w-full border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">Unassigned</option>
            {users.map((u) => (
              <option key={u.id} value={u.id}>
                {u.displayName ?? u.email}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Boundary Type <span className="text-red-500">*</span>
          </label>
          <div className="flex gap-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="territoryType"
                value="STATE"
                checked={type === 'STATE'}
                onChange={() => setType('STATE')}
                className="text-blue-600 focus:ring-blue-500"
              />
              <span className="text-sm text-gray-700 dark:text-gray-300">State-based</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="territoryType"
                value="ZIP_CODE"
                checked={type === 'ZIP_CODE'}
                onChange={() => setType('ZIP_CODE')}
                className="text-blue-600 focus:ring-blue-500"
              />
              <span className="text-sm text-gray-700 dark:text-gray-300">ZIP Prefix-based</span>
            </label>
          </div>
        </div>

        {type === 'STATE' && (
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              States
              {selectedStates.length > 0 && (
                <span className="font-normal text-gray-400 dark:text-gray-500 ml-1">({selectedStates.length} selected)</span>
              )}
            </label>
            <div className="flex flex-wrap gap-1">
              {US_STATES.map((code) => (
                <button
                  key={code}
                  type="button"
                  onClick={() => toggleState(code)}
                  className={`px-2 py-1 rounded text-xs font-medium border transition-colors ${
                    selectedStates.includes(code)
                      ? 'bg-blue-600 text-white border-blue-600'
                      : 'bg-white dark:bg-gray-700 text-gray-600 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:border-blue-400 dark:hover:border-blue-500 hover:text-blue-600 dark:hover:text-blue-400'
                  }`}
                >
                  {code}
                </button>
              ))}
            </div>
          </div>
        )}

        {type === 'ZIP_CODE' && (
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              ZIP Prefixes
              {zipCodes.length > 0 && (
                <span className="font-normal text-gray-400 dark:text-gray-500 ml-1">({zipCodes.length} added)</span>
              )}
            </label>
            <div className="flex gap-2 mb-2">
              <input
                type="text"
                value={zipInput}
                onChange={(e) => setZipInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addZips();
                  }
                }}
                placeholder="Enter 3-digit prefixes or 5-digit ZIPs"
                className="flex-1 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 dark:placeholder-gray-400 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <button
                type="button"
                onClick={addZips}
                className="border border-gray-300 dark:border-gray-600 px-3 py-2 rounded-md text-sm hover:bg-gray-50 dark:hover:bg-gray-700 dark:text-gray-300"
              >
                Add
              </button>
            </div>
            {zipCodes.length > 0 && (
              <div className="border border-gray-200 dark:border-gray-700 rounded-md p-2 max-h-32 overflow-y-auto dark:bg-gray-700/50">
                <div className="flex flex-wrap gap-1">
                  {zipCodes.map((zip) => (
                    <span
                      key={zip}
                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300"
                    >
                      {formatZip(zip)}
                      <button
                        type="button"
                        onClick={() => setZipCodes((prev) => prev.filter((z) => z !== zip))}
                        className="text-purple-600 dark:text-purple-400 hover:text-purple-900 dark:hover:text-purple-200 ml-0.5"
                        aria-label={`Remove ${zip}`}
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        <div className="flex justify-end gap-3 pt-1">
          <button
            type="button"
            onClick={onClose}
            className="border border-gray-300 dark:border-gray-600 px-4 py-2 rounded-md text-sm hover:bg-gray-50 dark:hover:bg-gray-700 dark:text-gray-300"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isSaving}
            className="bg-blue-600 text-white px-4 py-2 rounded-md text-sm hover:bg-blue-700 disabled:opacity-60"
          >
            {isSaving ? 'Creating...' : 'Create Territory'}
          </button>
        </div>
      </form>
    </div>
  );
}
